'use client';

import type { IconButtonProps, SpanProps } from '@chakra-ui/react';
import { ClientOnly, IconButton, Skeleton, Span } from '@chakra-ui/react';
import * as React from 'react';
import { LuMoon, LuSun } from 'react-icons/lu';
import { useColorMode } from '@providers/colorModeProvider';

export function ColorModeIcon() {
  const { colorMode } = useColorMode();
  const lightIcon = {
    color: 'black',
    _hover: {
      color: 'terracotta.700',
    },
    icon: <LuMoon />,
  };
  const darkIcon = {
    color: 'white',
    _hover: {
      color: 'terracotta.300',
    },
    icon: <LuSun />,
  };
  return colorMode === 'dark' ? darkIcon : lightIcon;
}

interface ColorModeButtonProps extends Omit<IconButtonProps, 'aria-label'> {}

export const ColorModeButton = React.forwardRef<
  HTMLButtonElement,
  ColorModeButtonProps
>(function ColorModeButton(props, ref) {
  const { toggleColorMode } = useColorMode();
  const { icon, ...iconStyles } = ColorModeIcon();
  return (
    <ClientOnly fallback={<Skeleton boxSize="8" />}>
      <IconButton
        onClick={toggleColorMode}
        variant="plain"
        aria-label="Toggle color mode"
        size="md"
        ref={ref}
        {...props}
        {...iconStyles}
        css={{
          _icon: {
            width: '5',
            height: '5',
          },
        }}
      >
        {icon}
      </IconButton>
    </ClientOnly>
  );
});

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"
        colorPalette="brand"
        colorScheme="light"
        ref={ref}
        {...props}
      />
    );
  },
);

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"
        colorPalette="brand"
        colorScheme="dark"
        ref={ref}
        {...props}
      />
    );
  },
);
